import React from 'react';
import { Box, BoxProps, SxProps, Theme } from '@mui/material';

/**
 * Responsive column configuration for different breakpoints
 */
export interface ResponsiveColumns {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

/**
 * Standard responsive grid presets
 */
export const GRID_PRESETS = {
  /**
   * Default cards layout: 1 -> 2 -> 3 -> 4 columns
   */
  cards: { xs: 1, sm: 2, md: 3, lg: 4 },
  /**
   * Wide cards layout: 1 -> 2 -> 2 -> 3 columns
   */
  wideCards: { xs: 1, sm: 2, md: 2, lg: 3 },
  /**
   * Compact layout: 2 -> 3 -> 4 -> 6 columns
   */
  compact: { xs: 2, sm: 3, md: 4, lg: 6 },
  /**
   * Single column on mobile, 2 columns on larger screens
   */
  twoColumn: { xs: 1, sm: 2, md: 2, lg: 2 },
  /**
   * Full width single column
   */
  singleColumn: { xs: 1, sm: 1, md: 1, lg: 1 },
} as const;

export type GridPreset = keyof typeof GRID_PRESETS;

export interface ResponsiveCardGridProps extends Omit<BoxProps, 'display'> {
  /**
   * Number of columns at each breakpoint, or a preset name
   * @default 'cards'
   */
  columns?: ResponsiveColumns | GridPreset;
  /**
   * Gap between grid items (MUI spacing units)
   * @default 2
   */
  gap?: number;
  /**
   * Children to render in the grid
   */
  children: React.ReactNode;
}

/**
 * Convert column count to CSS grid template
 */
function columnsToTemplate(columns: number): string {
  return `repeat(${columns}, 1fr)`;
}

/**
 * Resolve columns config from preset or custom values
 */
function resolveColumns(columns: ResponsiveColumns | GridPreset): ResponsiveColumns {
  if (typeof columns === 'string') {
    return GRID_PRESETS[columns];
  }
  return columns;
}

/**
 * Reusable responsive grid component for card layouts.
 * Provides consistent responsive column configurations across the application.
 *
 * @example
 * // Using default preset (1 -> 2 -> 3 -> 4 columns)
 * <ResponsiveCardGrid>
 *   {items.map(item => <Card key={item.id} />)}
 * </ResponsiveCardGrid>
 *
 * @example
 * // Using a named preset
 * <ResponsiveCardGrid columns="wideCards" gap={3}>
 *   {items.map(item => <WideCard key={item.id} />)}
 * </ResponsiveCardGrid>
 *
 * @example
 * // Custom column configuration
 * <ResponsiveCardGrid columns={{ xs: 1, sm: 2, lg: 5 }}>
 *   {items.map(item => <Card key={item.id} />)}
 * </ResponsiveCardGrid>
 */
export function ResponsiveCardGrid({
  columns = 'cards',
  gap = 2,
  children,
  sx,
  ...props
}: ResponsiveCardGridProps) {
  const resolvedColumns = resolveColumns(columns);

  const gridSx: SxProps<Theme> = {
    display: 'grid',
    gap,
    gridTemplateColumns: {
      xs: columnsToTemplate(resolvedColumns.xs || 1),
      sm: columnsToTemplate(resolvedColumns.sm || resolvedColumns.xs || 2),
      md: columnsToTemplate(resolvedColumns.md || resolvedColumns.sm || 3),
      lg: columnsToTemplate(resolvedColumns.lg || resolvedColumns.md || 4),
      xl: columnsToTemplate(resolvedColumns.xl || resolvedColumns.lg || resolvedColumns.md || 4),
    },
    ...sx,
  };

  return (
    <Box sx={gridSx} {...props}>
      {children}
    </Box>
  );
}

export default ResponsiveCardGrid;
